import { Flex, Spacing, Text } from '@billy/design-system'
import { type ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { LIVE_PREVIEW_DEBOUNCE_TIME } from '@features/invoices/constants/livePreviewDebounceTime'

import { useDebouncedValue } from '../../../../../../hooks/useDebouncedValue'
import { useForm } from '../../../../../../hooks/useForm'
import { InvoicingSettingsSection } from '../../../../enums/invoicingSettingsSection'
import { useRegisterInvoicingSettingsForm } from '../../../../hooks/useRegisterInvoicingSettingsForm'
import { useRegisterInvoicingSettingsPreviewPatch } from '../../../../hooks/useRegisterInvoicingSettingsPreviewPatch'
import { useZervantDocumentTemplateSettings } from '../../../../hooks/useZervantDocumentTemplateSettings'
import type {
  InvoicingSettingsFormSaveOptions,
  InvoicingSettingsFormSaveResult,
} from '../../../../types/invoicingSettingsFormSave'
import { usePredefinedLogoAndBrandingSettingsFormValues } from '../../hooks/usePredefinedLogoAndBrandingSettingsFormValues'
import { useUpdateLogoAndBrandingTemplateSettings } from '../../hooks/useUpdateLogoAndBrandingTemplateSettings'
import type { LogoAndBrandingSettingsFormSchema } from '../../types/logoAndBrandingSettingsFormSchema'
import { getLogoAndBrandingPreviewPatch } from '../../utils/getLogoAndBrandingPreviewPatch'
import { getLogoAndBrandingSettingsCompanyPayload } from '../../utils/getLogoAndBrandingSettingsCompanyPayload'
import { getLogoAndBrandingSettingsDefaultValues } from '../../utils/getLogoAndBrandingSettingsDefaultValues'
import { getLogoAndBrandingSettingsFormSchema } from '../../utils/getLogoAndBrandingSettingsFormSchema'
import { LogoAndBrandingSettingsFormFields } from '../LogoAndBrandingSettingsFormFields'

export const LogoAndBrandingSettingsForm = (): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { hasFailedToLoad, isLoading, predefinedLogoAndBrandingSettingsFormValues, refetch } =
    usePredefinedLogoAndBrandingSettingsFormValues()
  const { update: updateTemplate } = useUpdateLogoAndBrandingTemplateSettings()
  const { data: documentTemplateSettings } = useZervantDocumentTemplateSettings()
  const { Form, formState, getValues, reset, trigger, watch } = useForm<LogoAndBrandingSettingsFormSchema>({
    defaultValues: getLogoAndBrandingSettingsDefaultValues(),
    validationSchema: getLogoAndBrandingSettingsFormSchema(t),
  })

  const { isDirty } = formState
  const hasLoadedSettings = !isLoading && !hasFailedToLoad
  const formValues = watch()
  const debouncedTemplateColor = useDebouncedValue(formValues.templateColor, LIVE_PREVIEW_DEBOUNCE_TIME)

  const handleFormReset = useCallback(() => {
    reset(getValues())
  }, [getValues, reset])

  const handleSave = useCallback(
    async ({ updateSettings }: InvoicingSettingsFormSaveOptions): Promise<InvoicingSettingsFormSaveResult> => {
      if (!hasLoadedSettings) {
        throw new Error('Logo and branding settings are not loaded')
      }

      const isValid = await trigger()

      if (!isValid) {
        return 'invalid'
      }

      const values = getValues()

      await Promise.all([
        updateSettings({
          values: {
            company: getLogoAndBrandingSettingsCompanyPayload(values, queryClient),
          },
        }),
        updateTemplate(values),
      ])

      // No data invalidation needed:
      // The settings writer seeds its cache from the PUT response. The template mutation reads its
      // own cache, then invalidates that query before this promise resolves. Logo upload/delete
      // updates logoUrl on the form; that query only fills the field on mount.
      return 'saved'
    },
    [getValues, hasLoadedSettings, queryClient, trigger, updateTemplate],
  )

  useRegisterInvoicingSettingsForm({ isDirty, resetForm: handleFormReset, save: handleSave })

  const previewPatch =
    isDirty && hasLoadedSettings
      ? getLogoAndBrandingPreviewPatch({
          documentTemplateSettings,
          savedLogoId: predefinedLogoAndBrandingSettingsFormValues.logoId,
          values: {
            ...formValues,
            templateColor: debouncedTemplateColor,
          },
        })
      : undefined

  useRegisterInvoicingSettingsPreviewPatch(previewPatch)

  // Replace the empty defaults with server values once the settings document and the template have loaded.
  // A failed fetch leaves those defaults in place and the fields unmounted, so Save cannot send them.
  // Skip this while the user has unsaved edits, so a refetch cannot wipe them.
  // isDirty is not a dependency. Save awaits the template refetch, then the host calls
  // reset(getValues()). A second run once the form is clean would reset from predefined
  // values, whose logoUrl still comes from the logo query this save does not invalidate.
  useEffect(() => {
    if (!hasLoadedSettings || isDirty) {
      return
    }

    reset(getLogoAndBrandingSettingsDefaultValues(predefinedLogoAndBrandingSettingsFormValues))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDirty is a guard, not a dependency: a second run after save would reset from predefined values with a stale logoUrl
  }, [hasLoadedSettings, predefinedLogoAndBrandingSettingsFormValues, reset])

  return (
    <Flex flexDirection="column" gap={Spacing.S24}>
      <Text variant="h2">{t('invoicing_settings.logo_and_branding')}</Text>
      <Form>
        <LogoAndBrandingSettingsFormFields
          hasFailedToLoad={hasFailedToLoad}
          isLoading={isLoading}
          onRetry={refetch}
          trackingSection={InvoicingSettingsSection.LogoAndBranding}
        />
      </Form>
    </Flex>
  )
}
